<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end mb-3 pb-1">
        <b-button v-if="isSaving" variant="primary" size="lg" disabled>
          <b-spinner small />
          {{ 'saving' | i18n }}
        </b-button>
        <b-button v-else-if="isCommonAdmin" variant="primary" size="lg" @click="submit" :disabled="saveDisabled">
          {{ 'save' | i18n }}
        </b-button>
      </div>
      <div class="poppins overflow-x-hidden overflow-auto h-100">
        <div>
          <BlockHeader name="Details" class="mt-0" />
          <div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="group.name" placeholder="Name" />
            </div>
          </div>
        </div>
        <div>
          <BlockHeader name="Milestones">
            <b-button variant="primary" size="sm" @click="addItem" style="margin-left: 10px">+</b-button>
          </BlockHeader>
          <draggable
            :list="group.items"
            :group="{ name: 'items', put: false }"
            handle=".handle"
            :force-fallback="true"
            :options="{ forceFallback: true }"
          >
            <div class="row" v-for="item in group.items" :key="item.id">
              <div class="col-lg-6 col-sm-12 col-xs-12 handle-wrapper">
                <span class="handle"><BIconJustify /></span>
                <Input v-model="item.name" placeholder="Name" />
              </div>
              <div class="col-lg-5 col-sm-11 col-xs-11">
                <Select v-model="item.template" :options="templateOptions" form-group-id="Project Template" placeholder="Project Template" />
              </div>
              <div class="col-lg-1 col-sm-1 col-xs-1 pt-3">
                <b-button v-b-tooltip.hover :title="'Delete' | i18n" class="close float-none mr-3 mt-4" variant="light" @click="deleteLine(item.id)">
                  <span aria-hidden="true">&times;</span>
                </b-button>
              </div>
            </div>
          </draggable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { BIconJustify } from 'bootstrap-vue';

import Loader from '../../../common/loader';
import Input from '../../../common/input';
import Select from '../../../common/select';
import BlockHeader from '../../../common/block-header';
import Notification from '../../../common/notification';

import api from '../../../../api/index';

import { DeleteRowMixin, ProjectsMixin, AuthMixin } from '../../../../../mixins';

export default {
  title: 'Upsert Milestone Group',
  components: {
    Loader,
    Input,
    Select,
    BlockHeader,
    draggable,
    BIconJustify,
  },
  mixins: [DeleteRowMixin, ProjectsMixin, AuthMixin],
  beforeRouteLeave(to, from, next) {
    if (this.disablePrevent) {
      return next();
    }
    const answer = window.confirm(this.$translate('You have not saved your progress Are you sure you want to leave this page'));
    if (answer) {
      next();
    } else {
      next(false);
    }
  },
  data() {
    return {
      templateOptions: [],
      disablePrevent: true,
      group: {
        name: '',
        items: [],
      },
      id: null,
      loaded: false,
      isSaving: false,
    };
  },
  computed: {
    saveDisabled() {
      return !this.group.name || !this.group.items.length || this.group.items.some((i) => !i.name || !i.template);
    },
  },
  watch: {
    group: {
      handler: function () {
        if (this.loaded) {
          this.disablePrevent = false;
        }
      },
      deep: true,
    },
  },
  async created() {
    const templates = await this.projectsService.getTemplates();
    this.templates = templates.data;
    this.templateOptions = templates.data.map((i) => ({ value: i.id, text: i.name }));

    this.id = this.$route.params.id;
    if (this.id) {
      try {
        const { data: group } = await api.milestoneGroup.getById(this.id);
        this.group = {
          name: group.name,
          items: group.items.map((i) => ({
            name: i.name,
            template: i.template.id,
            id: this.getID(),
          })),
        };
      } catch (e) {
        console.log('Error:', e);
      }
    } else {
      this.group.items.push({
        name: '',
        template: null,
        id: this.getID(),
      });
    }

    setTimeout(() => {
      this.loaded = true;
    }, 100);
  },
  methods: {
    deleteLine(id) {
      this.deleteSelectedRows(
        {},
        async () => {
          this.group.items = this.group.items.filter((i) => i.id !== id);
        },
        false
      );
    },
    getID() {
      return `${Math.round(Math.random() * 10000)}_${new Date().getTime()}`;
    },
    async submit() {
      this.isSaving = true;
      this.disablePrevent = true;

      try {
        const data = {
          name: this.group.name,
          items: this.group.items.map((i) => ({
            name: i.name,
            template: i.template,
          })),
        };
        if (this.id) {
          await api.milestoneGroup.update({ id: this.id, ...data });
        } else {
          await api.milestoneGroup.create(data);
        }
        this.$router.go(-1);
        Notification({ type: 'success', message: this.$translate('success') });
      } catch (err) {
        this.disablePrevent = false;
        Notification({ type: 'error', message: err.message });
      } finally {
        this.isSaving = false;
      }
    },
    addItem() {
      this.group.items.push({
        name: '',
        template: null,
        id: this.getID(),
      });
    },
  },
};
</script>

<style scoped lang="scss">
.handle {
  cursor: move;
  font-size: 21px;
  margin-right: 15px;
  margin-top: 35px;
}
.handle-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  .form-group {
    width: 100%;
  }
}
.overflow-x-hidden {
  overflow-x: hidden!important;
}
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
